import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import CopyToClipboardButton from "@/components/shared/CopyToClipboardButton";
import DownloadSection from "./DownloadSection";

const OtherClientsIntegration: React.FC<{
  dashboardUrl: string;
  projectDeveloper: string;
}> = ({ dashboardUrl, projectDeveloper }) => {
  const { t, language } = useLanguage();

  const projectDeveloperFolder =
    projectDeveloper === "Tree.ly, Dornbirn" ? "treely" : "wkss";

  const filesToDownload = [
    `/integrations/sustainabilityBadge/${projectDeveloperFolder}/${language}/sustainability-badge.png`,
    `/integrations/sustainabilityBadge/${projectDeveloperFolder}/${language}/sustainability-badge.svg`,
  ];
  const fileName = filesToDownload[0].split("/").pop();

  // Helper function to render HTML content safely
  const renderHTML = (htmlString: string) => {
    return { __html: htmlString };
  };

  return (
    <div className="space-y-6">
      <ol className="list-none space-y-8 p-0 m-0">
        {/* Step 1 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            1
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step1Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.otherClientsIntegration.step1Description")
            )}
          />
          <DownloadSection fileUrls={filesToDownload} />
        </li>

        {/* Step 2 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            2
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step2Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.otherClientsIntegration.step2Description")
            )}
          />
        </li>

        {/* Step 3 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            3
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step3Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.otherClientsIntegration.step3Description")
            )}
          />
        </li>

        {/* Step 4 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            4
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step4Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.otherClientsIntegration.step4DescriptionPoint1"
                )
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.otherClientsIntegration.step4DescriptionPoint2",
                  {
                    fileName: `<span style="font-style: italic">${fileName}</span>`,
                  }
                )
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.otherClientsIntegration.step4DescriptionPoint3"
                )
              )}
            />
          </ul>
        </li>

        {/* Step 5 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            5
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step5Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.otherClientsIntegration.step5DescriptionPoint1"
                )
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.otherClientsIntegration.step5DescriptionPoint2"
                )
              )}
            />
            <span className="text-gray-700" style={{ fontStyle: "italic" }}>
              {dashboardUrl}
            </span>
            <CopyToClipboardButton text={dashboardUrl} />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t(
                  "integrationGuide.otherClientsIntegration.step5DescriptionPoint3"
                )
              )}
            />
          </ul>
        </li>

        {/* Step 6 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            6
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step6Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.otherClientsIntegration.step6Description")
            )}
          />
        </li>

        {/* Step 7 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            7
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.otherClientsIntegration.step7Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.otherClientsIntegration.step7Description")
            )}
          />
        </li>
      </ol>
    </div>
  );
};

export default OtherClientsIntegration;
